<template>
    <PuzzleCell
        :class="{
            'c-grid__cell--input': true,
            'c-grid__cell--solution': isSolutionCell,
            'c-grid__cell--selected': isSelected,
            'c-grid__cell--current-selected': isCurrentsSelected,
            'c-grid__cell--invalid': markAsInvalidNextChar,
            'c-grid__cell--found': wordIsFound && !isAnimating,
            'c-grid__cell--highlight-Bonus': !wordIsFound && allNonBonusWordsFound
        }"
        :column="column"
        :row="row"
        :text="cell.char ? cell.char.toUpperCase() : ''"
        :color="cell.color"
        :placeholderText="cell.placeholder"
        :data-index="wordIsFound ? cell.id : false"
        @mousedown.native="handleMouseDown"
        @touchstart.native.prevent="handleMouseDown"
        @mouseover.native="handleMouseover"
    />
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import { find } from 'lodash'
  import gsap from "gsap"

  import PuzzleCell from '@/components/puzzle/PuzzleCell'

  export default {
    name: 'PuzzleCellInput',

    props: ['cell', 'isAdmin'],

    methods: {
      handleMouseDown () {
        this.$store.commit('setIsSelectingAnswer', { isSelectingAnswer: true })
        this.selectCell()
      },

      handleMouseover () {
        this.selectCell()
      },

      selectCell () {
        // !Note: this function is also called in PuzzleGrid component through $refs for touch events

        // User isn't swiping
        if (!this.isSelectingAnswer) {
          return
        }

        // Can't select cells of words that have been found
        if (this.wordIsFound) {
          return
        }

        // Don't trigger when still hovering over same cell
        if (this.currentSelectedCell && this.currentSelectedCell.id === this.cell.id) {
          return
        }

        // Only connect neighbouring cells
        if (this.currentSelectedCell && !this.isNeighbourOfSelectedCell) {
          return
        }

        this.$store.commit('setCurrentSelectedCell', { cell: this.cell })
        this.toggleAnswerCell()
      },

      toggleAnswerCell () {
        this.$store.commit('toggleSelectedCell', { cell: this.cell })
      }
    },

    computed: {
      ...mapGetters([
        'selectedWordId',
        'allNonBonusWordsFound',
        'correctNextCell'
      ]),

      ...mapState({
        puzzle: state => state.currentPuzzle,
        answers: state => state.answers,
        gridSettings: state => state.gridSettings,
        size: state => state.gridSettings.cellSize,
        isSelectingAnswer: state => state.gridSettings.isSelectingAnswer,
        currentSelectedCell: state => state.gridSettings.currentSelectedCell,
        selectedCells: state => state.gridSettings.selectedCells,
        foundWordIds: state => state.foundWordIds,
        foundWordIdToAnimate: state => state.foundWordIdToAnimate
      }),

      isSolutionCell () {
        return false
      },

      isSelected () {
        return find(this.selectedCells, item => item.row === this.row && item.column === this.column)
      },

      isCurrentsSelected () {
        return this.currentSelectedCell && this.currentSelectedCell.id === this.cell.id
      },

      markAsInvalidNextChar () {
        // If this letter is the same as the next correct letter, but is a different cell
        // We mark this cell as invalid
        const correctNextCell = this.correctNextCell

        if (!correctNextCell || correctNextCell.char.toLowerCase() !== this.cell.char.toLowerCase()) {
          return false
        }

        if (this.isSelected) {
          return false
        }

        if (!this.isNeighbourOfSelectedCell) {
          return false
        }

        if (correctNextCell.id === this.cell.id) {
          return false
        }

        return true
      },

      isNeighbourOfSelectedCell () {
        const currentSelectedCell = this.currentSelectedCell
        if (!currentSelectedCell) {
            return false
        }

        const row = this.row
        const column = this.column

        // horizontal neighbour
        if (row === currentSelectedCell.row && (column -1 === currentSelectedCell.column || column +1 === currentSelectedCell.column)) {
          return true
        }

        // vertical
        if (column === currentSelectedCell.column && (row -1 === currentSelectedCell.row || row +1 === currentSelectedCell.row)) {
          return true
        }

        return false
      },

      wordIsFound () {
        return this.foundWordIds.indexOf(this.cell.word_id) !== -1
      },

      isAnimating () {
        return this.foundWordIdToAnimate === this.cell.word_id
      },

      row () {
        return this.cell.row
      },

      column () {
        return this.cell.column
      }
    },

    components: {
      PuzzleCell
    }
  }
</script>
